// routes
import { PATH_APP } from '../routes/paths';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isVariableSet = (value: any) => !(value === '' || value === undefined);
const throwIfAnyVariableIsNotSet = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  envVariablesObject: Record<string, any>,
  variableKeyExceptions: string[] = []
) => {
  Object.keys(envVariablesObject).forEach((key: string) => {
    if (!variableKeyExceptions.includes(key) && !isVariableSet(envVariablesObject[key])) {
      throw new Error(`Variable not set: ${key}`);
    }
  });
};

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/) ||
    // ngrok URLs for development
    window.location.hostname.includes('.ngrok-free.app') ||
    window.location.hostname.includes('.ngrok.io') ||
    // LocalTunnel URLs for development
    window.location.hostname.includes('.loca.lt') ||
    // RFC1918 private networks for local network testing
    window.location.hostname.match(
      /^(?:10\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|172\.(?:1[6-9]|2[0-9]|3[0-1])\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|192\.168\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))$/
    )
);

// Inject envs from '.env' when running locally (vite), otherwise use runtime env added to window (nginx)
// In offline mode, window.env might be an empty object (from SW fallback), so we merge with import.meta.env
const env = isLocalhost ? import.meta.env : { ...import.meta.env, ...window.env };

export const VITE_ENV = {
  showLogs: env.VITE_SHOW_LOGS,
  env: env.VITE_ENV,
  // This is only used for local development. See 'npm run start' for apiJwt injection
  apiJwt: env.VITE_API_JWT,
  isLocalhost,
  //
  devRedirectUrl: env.VITE_DEV_REDIRECT_URL,
  tp2Url: env.VITE_TP2_URL,
  tp3Url: env.VITE_TP3_URL,
  workManagementUrl: env.VITE_WORK_MANAGEMENT_URL,
  authUrl: env.VITE_AUTH_URL,
  cookieYesUrl: env.VITE_COOKIEYES_URL,
  assets: {
    maxCacheAssets: env.VITE_MAX_CACHE_ASSETS || '1250',
    maxRenderedFeatures: env.VITE_MAX_RENDERED_FEATURES || '700',
    maxAssetsPerRequest: env.VITE_MAX_ASSETS_PER_REQUEST || '500',
  },
  workTasks: {
    maxStorageUtilization: env.VITE_WORK_TASKS_MAX_STORAGE_UTILIZATION || '90',
    criticalStorageUtilization: env.VITE_WORK_TASKS_CRITICAL_STORAGE_UTILIZATION || '95',
    minAvailableBytes: env.VITE_WORK_TASKS_MIN_AVAILABLE_BYTES || '52428800', // 50MB
    maxCachedTasks: env.VITE_WORK_TASKS_MAX_CACHED_TASKS || '100000',
    evictionBatchSize: env.VITE_WORK_TASKS_EVICTION_BATCH_SIZE || '100',
    criticalEvictionBatchSize: env.VITE_WORK_TASKS_CRITICAL_EVICTION_BATCH_SIZE || '200',
  },
  posthog: {
    key: env.VITE_POSTHOG_KEY,
    host: env.VITE_POSTHOG_HOST,
    // Analytics capture is gated separately from feature flags. Default-off /
    // GDPR-safe: only the string 'true' (case-insensitive) enables analytics.
    // Feature flags work regardless of this value (US-only analytics; flags
    // everywhere).
    analyticsEnabled: env.VITE_POSTHOG_ANALYTICS_ENABLED?.toLowerCase() === 'true',
  },
  cognito: {
    hostedLoginUrl: env.VITE_COGNITO_HOSTED_LOGIN_URL,
    clientId: env.VITE_COGNITO_CLIENT_ID,
    callbackUrl: env.VITE_COGNITO_CALLBACK_URL,
  },
  apiKeys: {
    mapbox: env.VITE_MAPBOX_API_KEY,
    openlayers: env.VITE_OPENLAYERS_API_KEY,
  },
  map: {
    longPressDurationMs: env.VITE_LONG_PRESS_DURATION_MS || '600',
  },
};

throwIfAnyVariableIsNotSet(VITE_ENV, ['apiJwt', 'devRedirectUrl']);

if (VITE_ENV.posthog) {
  throwIfAnyVariableIsNotSet(VITE_ENV.posthog, []);
}

// ROOT PATH AFTER LOGIN SUCCESSFUL
export const PATH_AFTER_LOGIN = PATH_APP.root;

// Map
// ----------------------------------------------------------------------
type Themes = 'streets' | 'outdoors' | 'light' | 'dark' | 'satellite' | 'satelliteStreets';
export const MAP: {
  mapbox: {
    accessToken: string;
    themes: {
      [key in Themes]: string;
    };
    defaultTheme: Themes;
    baseSettings: {
      minZoom: number;
      center: number[];
      zoom: number;
    };
  };
  openLayers: {
    baseSettings: {
      minZoom: number;
      maxZoom: number;
      center: number[];
      zoom: number;
    };
    apiKey: string;
    baseMapsUrl: string;
    baseMapStyles: string[];
    defaultBaseMapStyle: string;
  };
} = {
  mapbox: {
    accessToken: VITE_ENV.apiKeys.mapbox,
    themes: {
      streets: 'mapbox://styles/mapbox/streets-v12',
      outdoors: 'mapbox://styles/mapbox/outdoors-v12',
      light: 'mapbox://styles/mapbox/light-v11',
      dark: 'mapbox://styles/mapbox/dark-v11',
      satellite: 'mapbox://styles/mapbox/satellite-v9',
      satelliteStreets: 'mapbox://styles/mapbox/satellite-streets-v12',
    },
    defaultTheme: 'satelliteStreets',
    baseSettings: {
      minZoom: 1,
      center: [-74.5, 40],
      zoom: 9,
    },
  },
  openLayers: {
    baseSettings: {
      minZoom: 1,
      maxZoom: 22,
      center: [-104.95040866597498, 39.909569576144996],
      zoom: 9,
    },
    apiKey: VITE_ENV.apiKeys.openlayers,
    baseMapsUrl: 'https://basemaps-api.arcgis.com/arcgis/rest/services/styles',
    baseMapStyles: [
      'ArcGIS:Imagery',
      'ArcGIS:Imagery:Standard',
      'ArcGIS:Imagery:Labels',
      'ArcGIS:Topographic',
      'ArcGIS:Topographic:Base',
      'ArcGIS:Terrain',
      'ArcGIS:Terrain:Base',
      'ArcGIS:Terrain:Detail',
      'ArcGIS:Hillshade:Dark',
      'ArcGIS:Hillshade:Light',
      'ArcGIS:DarkGray',
      'ArcGIS:DarkGray:Base',
      'ArcGIS:DarkGray:Labels',
      'ArcGIS:LightGray',
      'ArcGIS:LightGray:Base',
      'ArcGIS:LightGray:Labels',
      'ArcGIS:Navigation',
      'ArcGIS:NavigationNight',
      'ArcGIS:Streets',
      'ArcGIS:StreetsRelief',
      'ArcGIS:StreetsRelief:Base',
      'ArcGIS:StreetsNight',
    ],
    defaultBaseMapStyle: 'ArcGIS:Imagery',
  },
};
